import connect from 'components/Shared/connect';
import { match, NO_ELEMENT, rm } from 'utils';
import variantStyles from 'views/ProductPage/configurators/variant/themes/BGH/styles.scss';
import cn from 'classnames';
import { AttributeType } from 'models/AttributeType';
import { ToolTip } from 'components/Shared/ToolTip';
import { ConfiguratorProps, ModalProps } from 'views/ProductPage/configurators/index';
import { Attribute, useConfigurator } from 'views/ProductPage/configurators/useConfigurator';
import { rules } from 'views/ProductPage/configurators/rules';
import { masterAttribute } from 'views/ProductPage/configurators/variant';
import { ReactElement, useCallback, useEffect } from 'react';
import { ElfaConfigurator } from 'models/ElfaConfigurator';
import { Doors } from 'views/ProductPage/configurators/slidingDoor/doors';
import { DoorWidth } from 'views/ProductPage/configurators/slidingDoor/doorWidth';
import { Height } from 'views/ProductPage/configurators/slidingDoor/height';
import { AddonGroup } from 'models/AddonGroup';
import ProfileAddon from './profileAddons';
import DoorFilling from './doorFilling';
import { AddonActionPayload, selectAddon, unselectAddon } from 'views/ProductPage/addons/actions';
import { ElfaDoorSpec } from 'models/ElfaDoorSpec';
import {
  doorsKey,
  profileAddonKey,
  fillingAddonsKey,
  selectedFillingsKey,
  widthKey,
  profileKey,
  getFillings
} from '../../selector';
import styles from './styles.scss';

interface AddonEvents {
  selectAddon: (payload: AddonActionPayload) => void;
  unselectAddon: (payload: AddonActionPayload) => void;
}

interface OwnProps extends ConfiguratorProps, ModalProps {
  configurator: ElfaConfigurator;
  addons: { [key: string]: AddonGroup };
  isFamily: boolean;
}

interface MergedProps extends OwnProps, AddonEvents {
  cdnUrl: string;
}

const doorWidth = (width: number, doors: number) => Math.ceil(width / doors);

const widthVariant = (width: Pick<Attribute, 'values'>) => (doorSize: number) =>
  width.values
    .map(({ value }) => parseFloat(value))
    .filter(v => !Number.isNaN(v))
    .sort((a, b) => a - b)
    .find(value => doorSize <= value);

const getSelectedFilling = (doorId: number, values: ElfaDoorSpec[]) => {
  const existing = values.find(f => f.doorId === doorId);
  return existing ? existing : undefined;
};

const getTypeForFillingId = (group: AddonGroup, id: string) => {
  const addonProduct = group ? group.products.find(p => p.productId === id) : undefined;
  return addonProduct ? addonProduct.title : '';
};

const _Elfa = (props: MergedProps) => {
  const { onSelectChange } = useConfigurator();
  const { attributes, configurator } = useConfigurator<ElfaConfigurator>();
  const validate = rules(props);
  const autoAdjustValue = false;
  const { isMaster, isMasterSelected, hasMasterConstraint } = masterAttribute({
    configurator,
    configAttributes: attributes
  });
  const findWidthVariant = widthVariant(
    configurator.attributes.find(a => a.id === widthKey) || { values: [] }
  );

  const numDoors = configurator.values && parseInt(configurator.values[doorsKey]);
  const profileAddons = configurator.elfaAddons && configurator.elfaAddons[profileAddonKey];
  const fillingAddons = configurator.elfaAddons && configurator.elfaAddons[fillingAddonsKey];

  const setSelectedFilling = useCallback(
    (
      doorId: number,
      type: string,
      name: string,
      productId: string,
      artNo: string,
      selectedFillings: ElfaDoorSpec[],
      useOnAllDoors: boolean
    ) => {
      const fillings = selectedFillings.map(f => {
        if (useOnAllDoors || f.doorId === doorId) {
          return {
            doorId: f.doorId,
            type,
            name,
            productId,
            artNo
          };
        }

        return f;
      });

      const existing = fillings.find(f => f.doorId === doorId);
      if (!existing) {
        fillings.push({ doorId, type, name, productId, artNo });
      }

      onSelectChange(selectedFillingsKey, JSON.stringify(fillings));
    },
    [onSelectChange]
  );

  const selectDoorFilling = (
    type: string,
    name: string,
    productId: string,
    artNo: string,
    doorId: number,
    useOnAllDoors: boolean
  ) => {
    setSelectedFilling(
      doorId,
      type,
      name,
      productId,
      artNo,
      getFillings(configurator.values),
      useOnAllDoors
    );
  };

  useEffect(() => {
    const width = configurator.values && parseInt(configurator.values[widthKey]);
    if (width && numDoors) {
      const variantWidth = findWidthVariant(doorWidth(width, numDoors));
      if (variantWidth) {
        onSelectChange(doorsKey, numDoors.toString());
        onSelectChange(widthKey, variantWidth.toString(), { emit: false });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (numDoors) {
      const name = configurator.values && configurator.values['defaultFillingName'];
      const productId = configurator.values && configurator.values['defaultFillingId'];
      const artNo = configurator.values && configurator.values['defaultFillingArtNo'];

      if (name && productId && artNo) {
        const type = getTypeForFillingId(fillingAddons, productId);
        const fillings = getFillings(configurator.values);

        if (type) {
          // Add missing door fillings if any
          for (let i = 0; i < numDoors; i++) {
            if (!fillings.some(f => f.doorId === i)) {
              setSelectedFilling(i, type, name, productId, artNo, fillings, false);
            }
          }
        }
      }
    }
  }, [props.configurator.values, fillingAddons, numDoors, configurator.values, setSelectedFilling]);

  const component = match<Attribute, ReactElement>()
    .when(
      a => a.id === 'widthMm',
      a => (
        <DoorWidth
          {...{ ...a, validate, findWidthVariant, autoAdjustValue, calcDoorWidth: doorWidth }}
        />
      )
    )
    .when(
      a => a.id === 'heightMm',
      a => <Height {...{ ...a, validate }} />
    )
    .when(
      a => a.id === 'doors',
      a => (
        <Doors
          {...{
            ...a,
            validate,
            findWidthVariant,
            calcDoorWidth: doorWidth,
            autoAdjustValue,
            attributeType: a.type
          }}
        />
      )
    )
    .or(<></>)
    .toFunc();

  const AttributeComponent = (a: Attribute) => {
    const shouldShow = isMaster(a.id) || isMasterSelected();
    if (hasMasterConstraint && !shouldShow) {
      return NO_ELEMENT;
    }
    const comp = component(a);

    return (
      <div
        key={a.name}
        className={cn(variantStyles.cell, {
          [variantStyles.two]: a.type !== AttributeType.Image,
          [variantStyles.one]: a.type === AttributeType.Image
        })}
      >
        <label>{a.toolTip && <ToolTip text={a.toolTip} />}</label>
        {comp}
      </div>
    );
  };

  const ProfileComponent = (profileAddons: AddonGroup) => {
    const defaultValue = configurator.values && configurator.values[profileKey];

    return profileAddons.products ? (
      <div className={cn(variantStyles.cell, variantStyles.two)}>
        <ProfileAddon
          label={rm('product_configurator_elfa_profile_header')}
          products={profileAddons.products}
          default={defaultValue}
        />
      </div>
    ) : (
      NO_ELEMENT
    );
  };

  const DoorsComponent = (numDoors: number) => {
    const fillingModalHeader = rm('product_configurator_elfa_filling_modal_header');
    const fillingModalSame = rm('product_configurator_elfa_filling_modal_same');
    const fillingModalClose = rm('product_configurator_elfa_filling_modal_close');
    const fillingModalOpen = rm('product_configurator_elfa_filling_modal_open');
    const fillingModalDoorName = rm('product_configurator_elfa_filling_modal_doorname');
    const doors = [];
    const selectedFillings = getFillings(configurator.values);

    for (let i = 0; i < numDoors; i++) {
      doors.push(
        <DoorFilling
          key={i}
          addons={fillingAddons}
          doorId={i}
          defaultFilling={getSelectedFilling(i, selectedFillings)}
          selectDoorFilling={selectDoorFilling}
          fillingModalHeader={fillingModalHeader}
          fillingModalSame={fillingModalSame}
          fillingModalClose={fillingModalClose}
          fillingModalOpen={fillingModalOpen}
          fillingModalDoorName={fillingModalDoorName}
          cdnUrl={props.cdnUrl}
        />
      );
    }

    return doors.length > 0 ? (
      <div className={cn(variantStyles.one, styles.doors)} style={{ width: '100%' }}>
        {doors}
      </div>
    ) : (
      NO_ELEMENT
    );
  };

  return (
    <div
      className={cn(variantStyles.base, {
        [variantStyles.mobile]: props.isMobile
      })}
    >
      {attributes.map(AttributeComponent)}
      {profileAddons ? ProfileComponent(profileAddons) : NO_ELEMENT}
      {numDoors ? DoorsComponent(numDoors) : NO_ELEMENT}
    </div>
  );
};

export const Elfa = connect<MergedProps, OwnProps>(
  ({ appShell }) => ({
    cdnUrl: appShell.links.cdnUrl
  }),
  dispatch => ({
    selectAddon: (payload: AddonActionPayload) => dispatch(selectAddon(payload)),
    unselectAddon: (payload: AddonActionPayload) => dispatch(unselectAddon(payload))
  })
)(_Elfa);
